import React, { useEffect, useId, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
    FinanceDashboardAttentionProject,
    FinanceDashboardCalculationState,
    FinanceDashboardDebtProject,
    FinanceDashboardGrossProfitByPeriod,
    FinanceDashboardGrossProfitByPeriodStatus,
    FinanceDashboardProfitBelowPlanProject,
    FinanceDashboardProfitPlanProject,
    financeDashboardApi,
    getFinanceDashboardExpiresAt,
} from '@entities/financeDashboard';
import { formatPrice } from '@shared/helpers';
import { CustomModal } from '@shared/ui';

import cls from './FinanceDashboard.module.css';
import { ProfitProjectSort, sortProfitProjects } from '../model/sortProfitProjects';

import type { TFunction } from 'i18next';

interface FinanceDashboardProps {
    apiUrl: string;
    reportsUrl: string;
    companyName?: string;
}

interface AttentionSignal {
    id: string;
    projectId: number;
    projectName: string;
    title: string;
    description: string;
    value: number;
    kind: 'critical' | 'warning' | 'debt';
}

const formatMoney = (value: number, currencySign: string) => {
    const normalizedValue = Math.abs(value) < 0.5 ? 0 : value;

    return formatPrice(normalizedValue, { currencySign, maximumFractionDigits: 0 });
};

const formatPercent = (value: number | null, locale: string, fractionDigits = 1) =>
    value === null
        ? '—'
        : `${new Intl.NumberFormat(locale, {
              maximumFractionDigits: fractionDigits,
          }).format(value)}%`;

const formatCompactMoney = (value: number, locale: string) =>
    new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: Math.abs(value) >= 1_000_000 ? 1 : 0,
    }).format(value);

const getSignalIcon = (kind: AttentionSignal['kind']) => {
    if (kind === 'critical') return '!';
    if (kind === 'debt') return '₽';

    return '↘';
};

const formatCalculationTime = (calculatedAt: string | null, locale: string, t: TFunction) => {
    if (!calculatedAt) return t('FinanceDashboard.freshness.calculationTimeUnknown');

    const date = new Date(calculatedAt);
    if (Number.isNaN(date.getTime())) {
        return t('FinanceDashboard.freshness.calculationTimeUnknown');
    }

    return t('FinanceDashboard.freshness.calculatedAt', {
        date: new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date),
    });
};

const getFreshnessText = (expiresAt: number | null, now: number, t: TFunction) => {
    if (expiresAt === null) return t('FinanceDashboard.freshness.refreshData');

    const millisecondsLeft = expiresAt - now;
    if (millisecondsLeft <= 0) return t('FinanceDashboard.freshness.expired');

    const minutesLeft = Math.ceil(millisecondsLeft / 60_000);
    if (minutesLeft >= 60) {
        const hoursLeft = Math.floor(minutesLeft / 60);
        const remainingMinutes = minutesLeft % 60;
        const hoursText = t('FinanceDashboard.freshness.hours', {
            count: hoursLeft,
        });

        return remainingMinutes === 0
            ? t('FinanceDashboard.freshness.updatesInHours', { hours: hoursText })
            : t('FinanceDashboard.freshness.updatesInHoursAndMinutes', {
                  hours: hoursText,
                  minutes: remainingMinutes,
              });
    }

    return t('FinanceDashboard.freshness.updatesInMinutes', { count: minutesLeft });
};

const buildAttentionSignals = (
    forecastLoss: FinanceDashboardAttentionProject[],
    negativeBalance: FinanceDashboardAttentionProject[],
    profitBelowPlan: FinanceDashboardProfitBelowPlanProject[],
    highCustomerDebt: FinanceDashboardDebtProject[],
    currencySign: string,
    locale: string,
    t: TFunction
): AttentionSignal[] => [
    ...forecastLoss.map((project) => ({
        id: `forecast-loss-${project.projectId}`,
        projectId: project.projectId,
        projectName: project.projectName,
        title: t('FinanceDashboard.signals.forecastLoss.title'),
        description: t('FinanceDashboard.signals.forecastLoss.description', {
            value: formatMoney(project.profitForecast - project.plannedProfit, currencySign),
        }),
        value: project.profitForecast,
        kind: 'critical' as const,
    })),
    ...negativeBalance.map((project) => ({
        id: `negative-balance-${project.projectId}`,
        projectId: project.projectId,
        projectName: project.projectName,
        title: t('FinanceDashboard.signals.negativeBalance.title'),
        description: t('FinanceDashboard.signals.negativeBalance.description'),
        value: project.balance,
        kind: 'warning' as const,
    })),
    ...profitBelowPlan.map((project) => ({
        id: `profit-below-plan-${project.projectId}`,
        projectId: project.projectId,
        projectName: project.projectName,
        title: t('FinanceDashboard.signals.profitBelowPlan.title'),
        description: t('FinanceDashboard.signals.profitBelowPlan.description', {
            percent: formatPercent(project.deviationPercent, locale),
        }),
        value: project.profitForecast,
        kind: 'warning' as const,
    })),
    ...highCustomerDebt.map((project) => ({
        id: `high-debt-${project.projectId}`,
        projectId: project.projectId,
        projectName: project.projectName,
        title: t('FinanceDashboard.signals.highDebt.title'),
        description: t('FinanceDashboard.signals.highDebt.description', {
            percent: formatPercent(project.totalDebtPercent, locale),
        }),
        value: project.debt,
        kind: 'debt' as const,
    })),
];

const KpiCard = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <article className={cn(cls.kpiCard, className)}>{children}</article>
);

const ProjectLink = ({
    projectId,
    projectName,
    className,
}: {
    projectId: number;
    projectName: string;
    className?: string;
}) => (
    <a
        className={cn(cls.projectLink, className)}
        href={`/project/${projectId}/dashboard`}
        target="_blank"
        rel="noopener noreferrer"
        title={projectName}
    >
        {projectName}
    </a>
);

const AttentionSignalRow = ({
    signal,
    currencySign,
}: {
    signal: AttentionSignal;
    currencySign: string;
}) => (
    <div className={cls.signal}>
        <span className={cn(cls.signalIcon, cls[signal.kind])}>{getSignalIcon(signal.kind)}</span>
        <div>
            <strong>{signal.title}</strong>
            <p>
                <ProjectLink projectId={signal.projectId} projectName={signal.projectName} /> ·{' '}
                {formatMoney(signal.value, currencySign)}
            </p>
            <small>{signal.description}</small>
        </div>
    </div>
);

const LoadingState = () => {
    const { t } = useTranslation();

    return (
        <div className={cls.loadingState} aria-label={t('FinanceDashboard.loading.ariaLabel')}>
            <div className={cls.loadingHeader} />
            <div className={cls.loadingSummary} />
            <div className={cls.loadingCards}>
                {[1, 2, 3, 4].map((item) => (
                    <div key={item} />
                ))}
            </div>
            <div className={cls.loadingPanels}>
                <div />
                <div />
            </div>
        </div>
    );
};

const ErrorState = ({
    retry,
    calculationFailed = false,
}: {
    retry: () => void;
    calculationFailed?: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <div className={cls.errorState} role="alert">
            <span className={cls.errorIcon}>!</span>
            <h2>
                {t(
                    calculationFailed
                        ? 'FinanceDashboard.error.calculationTitle'
                        : 'FinanceDashboard.error.loadingTitle'
                )}
            </h2>
            <p>
                {t(
                    calculationFailed
                        ? 'FinanceDashboard.error.calculationDescription'
                        : 'FinanceDashboard.error.loadingDescription'
                )}
            </p>
            <button type="button" onClick={retry}>
                {t('FinanceDashboard.actions.retry')}
            </button>
        </div>
    );
};

const CalculationProgress = ({
    state,
    embedded = false,
}: {
    state: FinanceDashboardCalculationState;
    embedded?: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const { processed, total, percent } = state.progress;
    const isQueued = state.status === 'queued';
    const locale = i18n.resolvedLanguage ?? i18n.language;

    return (
        <section
            className={cn(cls.calculationCard, embedded && cls.calculationCardEmbedded)}
            aria-live="polite"
        >
            <div className={cls.calculationIcon}>₽</div>
            <div className={cls.calculationHeading}>
                <span>
                    {t(
                        isQueued
                            ? 'FinanceDashboard.calculation.queued'
                            : 'FinanceDashboard.calculation.processing'
                    )}
                </span>
                <strong>
                    {total === null
                        ? t('FinanceDashboard.calculation.preparing')
                        : `${Math.round(percent)}%`}
                </strong>
            </div>
            <div
                className={cls.calculationTrack}
                role="progressbar"
                aria-label={t('FinanceDashboard.calculation.progressAriaLabel')}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={total === null ? undefined : percent}
            >
                <i
                    className={cn(cls.calculationBar, total === null && cls.calculationBarPending)}
                    style={total === null ? undefined : { width: `${percent}%` }}
                />
            </div>
            <div className={cls.calculationDetails}>
                <span>
                    {total === null
                        ? t('FinanceDashboard.calculation.estimatingVolume')
                        : t('FinanceDashboard.calculation.processed', {
                              processed: new Intl.NumberFormat(locale).format(processed),
                              total: new Intl.NumberFormat(locale).format(total),
                          })}
                </span>
                <span>{t('FinanceDashboard.calculation.updatesAutomatically')}</span>
            </div>
            <p>{t('FinanceDashboard.calculation.backgroundHint')}</p>
        </section>
    );
};

const CalculationState = ({
    state,
    companyName,
}: {
    state: FinanceDashboardCalculationState;
    companyName?: string;
}) => {
    const { t } = useTranslation();

    return (
        <main className={cn(cls.dashboard, cls.calculationPage)}>
            <header className={cls.header}>
                <div>
                    <div className={cls.eyebrow}>{t('FinanceDashboard.header.eyebrow')}</div>
                    <h1>{t('FinanceDashboard.header.title')}</h1>
                    <p>
                        {t('FinanceDashboard.header.subtitle')}
                        {companyName ? ` · ${companyName}` : ''}
                    </p>
                </div>
                <div className={cn(cls.actualBadge, cls.calculationBadge)}>
                    <span /> {t('FinanceDashboard.calculation.inBackground')}
                </div>
            </header>
            <CalculationProgress state={state} />
        </main>
    );
};

const ProfitComparison = ({
    projects,
    currencySign,
    sort,
}: {
    projects: FinanceDashboardProfitPlanProject[];
    currencySign: string;
    sort: ProfitProjectSort;
}) => {
    const { t } = useTranslation();
    const visibleProjects = sortProfitProjects(projects, sort).slice(0, 6);
    const maximum = Math.max(
        1,
        ...visibleProjects.flatMap((project) => [
            Math.abs(project.plannedProfit),
            Math.abs(project.profitForecast),
        ])
    );

    if (visibleProjects.length === 0) {
        return <div className={cls.emptyPanel}>{t('FinanceDashboard.profitPlan.empty')}</div>;
    }

    return (
        <div className={cls.comparisonRows}>
            {visibleProjects.map((project, index) => {
                const planWidth = (Math.abs(project.plannedProfit) / maximum) * 100;
                const forecastWidth = (Math.abs(project.profitForecast) / maximum) * 100;

                return (
                    <div className={cls.comparisonRow} key={project.projectId}>
                        <ProjectLink
                            className={cls.projectName}
                            projectId={project.projectId}
                            projectName={project.projectName}
                        />
                        <div className={cls.dualBars}>
                            <div>
                                <i
                                    style={{
                                        width: `${planWidth}%`,
                                        animationDelay: `${index * 70}ms`,
                                    }}
                                />
                            </div>
                            <div>
                                <i
                                    className={cn(project.profitForecast < 0 && cls.negativeBar)}
                                    style={{
                                        width: `${forecastWidth}%`,
                                        animationDelay: `${index * 70 + 100}ms`,
                                    }}
                                />
                            </div>
                        </div>
                        <strong className={cn(project.profitForecast < 0 && cls.negativeValue)}>
                            {formatMoney(project.profitForecast, currencySign)}
                        </strong>
                    </div>
                );
            })}
        </div>
    );
};

const GrossProfitTrend = ({
    data,
    status,
    currencySign,
}: {
    data: FinanceDashboardGrossProfitByPeriod | null;
    status: FinanceDashboardGrossProfitByPeriodStatus;
    currencySign: string;
}) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage ?? i18n.language;
    const profitTrendClipId = `finance-dashboard-profit-trend-${useId().replace(/:/g, '')}`;
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    if (!data || data.labels.length === 0) {
        let message = t('FinanceDashboard.grossProfit.chartUnavailable');
        if (status === 'pending') message = t('FinanceDashboard.grossProfit.chartPending');
        if (status === 'unavailable') {
            message = t('FinanceDashboard.grossProfit.chartRestricted');
        }

        return <div className={cls.emptyTrend}>{message}</div>;
    }

    const width = 900;
    const height = 220;
    const topPadding = 12;
    const bottomPadding = 12;
    const progress = data.labels.map((_, index) => data.series[0]?.[index] ?? 0);
    const profit = data.labels.map((_, index) => data.series[1]?.[index] ?? 0);
    const values = [0, ...progress, ...profit];
    const rawMaximum = Math.max(...values);
    const rawMinimum = Math.min(...values);
    const padding = (rawMaximum - rawMinimum || 1) * 0.08;
    const maximum = rawMaximum + padding;
    const minimum = rawMinimum < 0 ? rawMinimum - padding : 0;
    const range = maximum - minimum || 1;
    const x = (index: number) =>
        data.labels.length === 1
            ? width / 2
            : 16 + (index / (data.labels.length - 1)) * (width - 32);
    const y = (value: number) =>
        topPadding + ((maximum - value) / range) * (height - topPadding - bottomPadding);
    const profitPoints = profit.map((value, index) => ({ x: x(index), y: y(value) }));
    const curveSegments = profitPoints
        .slice(1)
        .map((point, index) => {
            const start = profitPoints[index];
            const previous = profitPoints[index - 1] ?? start;
            const next = profitPoints[index + 2] ?? point;
            const firstControlX = start.x + (point.x - previous.x) / 6;
            const firstControlY = start.y + (point.y - previous.y) / 6;
            const secondControlX = point.x - (next.x - start.x) / 6;
            const secondControlY = point.y - (next.y - start.y) / 6;

            return `C ${firstControlX},${firstControlY} ${secondControlX},${secondControlY} ${point.x},${point.y}`;
        })
        .join(' ');
    const zeroY = y(0);
    const profitPath = `M ${profitPoints[0].x},${profitPoints[0].y} ${curveSegments}`;
    const profitAreaPath = `M ${profitPoints[0].x},${zeroY} L ${profitPoints[0].x},${
        profitPoints[0].y
    } ${curveSegments} L ${profitPoints[profitPoints.length - 1].x},${zeroY} Z`;
    const tickValues = Array.from({ length: 4 }, (_, index) => maximum - (range * index) / 3);
    const barWidth = Math.max(10, Math.min(34, width / data.labels.length / 2.4));
    const hoveredPeriod =
        hoveredIndex === null || data.labels[hoveredIndex] === undefined
            ? null
            : t(`FinanceDashboard.month.${data.labels[hoveredIndex]}`, data.labels[hoveredIndex]);

    return (
        <div className={cls.trendContent}>
            <div className={cls.chartNumber}>
                <strong>{formatMoney(data.profit, currencySign)}</strong>
                <span>
                    {t('FinanceDashboard.grossProfit.currentMonthPercent', {
                        percent: formatPercent(data.percent, locale),
                    })}
                </span>
            </div>
            <div className={cls.comboChart}>
                <div className={cls.yLabels}>
                    {tickValues.map((value, index) => (
                        <span key={index}>{formatCompactMoney(value, locale)}</span>
                    ))}
                </div>
                <div className={cls.plot} onMouseLeave={() => setHoveredIndex(null)}>
                    <svg
                        viewBox={`0 0 ${width} ${height}`}
                        role="img"
                        aria-label={t('FinanceDashboard.grossProfit.chartAriaLabel')}
                        preserveAspectRatio="none"
                    >
                        <defs>
                            <clipPath id={profitTrendClipId}>
                                <rect
                                    className={cls.profitTrendClip}
                                    x="0"
                                    y="0"
                                    width={width}
                                    height={height}
                                />
                            </clipPath>
                        </defs>
                        {tickValues.map((value, index) => (
                            <line
                                className={cls.gridLine}
                                x1="0"
                                x2={width}
                                y1={y(value)}
                                y2={y(value)}
                                key={index}
                            />
                        ))}
                        {progress.map((value, index) => {
                            const valueY = y(value);

                            return (
                                <rect
                                    className={cls.progressBar}
                                    x={x(index) - barWidth / 2}
                                    y={Math.min(valueY, zeroY)}
                                    width={barWidth}
                                    height={Math.max(1, Math.abs(valueY - zeroY))}
                                    rx="5"
                                    key={`${data.labels[index]}-progress`}
                                    style={{ animationDelay: `${index * 45}ms` }}
                                />
                            );
                        })}
                        <g clipPath={`url(#${profitTrendClipId})`}>
                            <path className={cls.profitArea} d={profitAreaPath} />
                            <path className={cls.profitLine} d={profitPath} />
                        </g>
                        {profit.map((value, index) => (
                            <circle
                                className={cls.profitPoint}
                                cx={x(index)}
                                cy={y(value)}
                                key={`${data.labels[index]}-profit`}
                                r="3.5"
                            />
                        ))}
                        {hoveredIndex !== null && data.labels[hoveredIndex] !== undefined && (
                            <line
                                className={cls.chartHoverLine}
                                x1={x(hoveredIndex)}
                                x2={x(hoveredIndex)}
                                y1="0"
                                y2={height}
                            />
                        )}
                        {data.labels.map((label, index) => {
                            const center = x(index);
                            const left = index === 0 ? 0 : (x(index - 1) + center) / 2;
                            const right =
                                index === data.labels.length - 1
                                    ? width
                                    : (center + x(index + 1)) / 2;
                            const period = t(`FinanceDashboard.month.${label}`, label);

                            return (
                                <rect
                                    className={cls.chartHitArea}
                                    x={left}
                                    y="0"
                                    width={right - left}
                                    height={height}
                                    key={`${label}-${index}-hit-area`}
                                    tabIndex={0}
                                    aria-label={`${period}: ${t(
                                        'FinanceDashboard.grossProfit.completion'
                                    )} ${formatMoney(progress[index], currencySign)}, ${t(
                                        'FinanceDashboard.grossProfit.profit'
                                    )} ${formatMoney(profit[index], currencySign)}`}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onFocus={() => setHoveredIndex(index)}
                                    onBlur={() => setHoveredIndex(null)}
                                />
                            );
                        })}
                    </svg>
                    {hoveredIndex !== null && hoveredPeriod !== null && (
                        <div
                            className={cls.chartTooltip}
                            style={{
                                left: `clamp(92px, ${
                                    (x(hoveredIndex) / width) * 100
                                }%, calc(100% - 92px))`,
                            }}
                        >
                            <strong>{hoveredPeriod}</strong>
                            <span>
                                <i className={cls.progressTooltipMarker} />
                                {t('FinanceDashboard.grossProfit.completion')}
                                <b>{formatMoney(progress[hoveredIndex], currencySign)}</b>
                            </span>
                            <span>
                                <i className={cls.profitTooltipMarker} />
                                {t('FinanceDashboard.grossProfit.profit')}
                                <b>{formatMoney(profit[hoveredIndex], currencySign)}</b>
                            </span>
                        </div>
                    )}
                    <div className={cls.xLabels}>
                        {data.labels.map((label, index) => {
                            const localizedLabel = t(`FinanceDashboard.month.${label}`, label);

                            return (
                                <span
                                    key={`${label}-${index}`}
                                    style={{ left: `${(x(index) / width) * 100}%` }}
                                >
                                    {localizedLabel.slice(0, 3)}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const FinanceDashboard = ({ apiUrl, reportsUrl, companyName }: FinanceDashboardProps) => {
    const { t, i18n } = useTranslation();
    const attentionRef = useRef<HTMLElement>(null);
    const queryClient = useQueryClient();
    const [signalsModalOpen, setSignalsModalOpen] = useState(false);
    const [calculationModalOpen, setCalculationModalOpen] = useState(false);
    const [profitSort, setProfitSort] = useState<ProfitProjectSort>('forecast');
    const [restartPending, setRestartPending] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const queryOptions = financeDashboardApi.getQueryOptions(apiUrl);
    const { data, isPending, isFetching, refetch } = useQuery(queryOptions);
    const locale = i18n.resolvedLanguage ?? i18n.language;
    const isBackgroundCalculation = Boolean(
        data?.result && (data.status === 'queued' || data.status === 'processing')
    );

    useEffect(() => {
        const interval = window.setInterval(() => setNow(Date.now()), 30_000);

        return () => window.clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!isBackgroundCalculation) setCalculationModalOpen(false);
    }, [isBackgroundCalculation]);

    const restartCalculation = () => {
        setRestartPending(true);
        financeDashboardApi.restart(apiUrl).then(
            (state) => {
                queryClient.setQueryData(queryOptions.queryKey, state);
                setRestartPending(false);
            },
            () => setRestartPending(false)
        );
    };

    if (isPending) return <LoadingState />;
    if (!data) {
        return (
            <ErrorState
                retry={() => {
                    refetch();
                }}
            />
        );
    }

    if (data.status === 'failed' && !data.result) {
        return <ErrorState calculationFailed retry={restartCalculation} />;
    }

    if (!data.result) {
        return <CalculationState state={data} companyName={companyName} />;
    }

    const { metrics, currency, economyStatus, attention, charts, availability } = data.result;
    const signals = buildAttentionSignals(
        attention.forecastLoss,
        attention.negativeBalance,
        attention.profitBelowPlan,
        attention.highCustomerDebt,
        currency.sign,
        locale,
        t
    );
    const riskCount = signals.length;
    const expiresAt = getFinanceDashboardExpiresAt(data);
    const calculatedAtTimestamp = data.calculatedAt ? Date.parse(data.calculatedAt) : Number.NaN;
    const freshnessReferenceTime = Number.isNaN(calculatedAtTimestamp)
        ? now
        : Math.max(now, calculatedAtTimestamp);
    const freshnessText =
        data.status === 'failed'
            ? t('FinanceDashboard.freshness.updateFailed')
            : getFreshnessText(expiresAt, freshnessReferenceTime, t);
    const calculationTime = formatCalculationTime(data.calculatedAt, locale, t);
    const backgroundProgressText =
        data.progress.total === null
            ? t('FinanceDashboard.calculation.refreshing')
            : t('FinanceDashboard.calculation.refreshingPercent', {
                  percent: Math.round(data.progress.percent),
              });
    const varianceIsPositive = metrics.profit.variance >= 0;
    const summaryIsCritical = economyStatus.priority <= 2;
    const summaryNeedsAttention = economyStatus.priority >= 3 && economyStatus.priority <= 7;
    let profitComparison = t('FinanceDashboard.summary.profitOnPlan');
    if (metrics.profit.variance > 0) {
        profitComparison = t('FinanceDashboard.summary.profitAbovePlan', {
            value: formatMoney(metrics.profit.variance, currency.sign),
        });
    } else if (metrics.profit.variance < 0) {
        profitComparison = t('FinanceDashboard.summary.profitBelowPlan', {
            value: formatMoney(Math.abs(metrics.profit.variance), currency.sign),
        });
    }
    const debtMaximum = Math.max(
        1,
        ...charts.customerDebt.projects.map((project) => project.value)
    );

    const scrollToAttention = () => {
        attentionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const showRisks = () => {
        if (signals.length > 3) {
            setSignalsModalOpen(true);
            return;
        }

        scrollToAttention();
    };

    return (
        <main className={cls.dashboard}>
            <header className={cls.header}>
                <div>
                    <div className={cls.eyebrow}>{t('FinanceDashboard.header.eyebrow')}</div>
                    <h1>{t('FinanceDashboard.header.title')}</h1>
                    <p>
                        {t('FinanceDashboard.header.subtitle')}
                        {companyName ? ` · ${companyName}` : ''}
                    </p>
                </div>
                <div className={cls.freshness}>
                    {isBackgroundCalculation ? (
                        <button
                            className={cls.backgroundCalculation}
                            type="button"
                            onClick={() => setCalculationModalOpen(true)}
                        >
                            <span /> {backgroundProgressText}
                        </button>
                    ) : (
                        <div
                            className={cn(
                                cls.actualBadge,
                                data.status === 'failed' && cls.staleBadge
                            )}
                        >
                            <span /> {freshnessText}
                        </div>
                    )}
                    <small>{calculationTime}</small>
                    <button
                        className={cls.refreshButton}
                        type="button"
                        onClick={restartCalculation}
                        disabled={isFetching || isBackgroundCalculation || restartPending}
                    >
                        {isFetching || isBackgroundCalculation || restartPending
                            ? t('FinanceDashboard.actions.refreshStarted')
                            : t('FinanceDashboard.actions.refreshNow')}
                    </button>
                </div>
            </header>

            <section className={cls.controls} aria-label={t('FinanceDashboard.controls.ariaLabel')}>
                <div className={cls.modeSwitch}>
                    <span className={cls.activeMode}>
                        {t('FinanceDashboard.controls.projectEconomics')}
                    </span>
                    <a href={reportsUrl}>{t('FinanceDashboard.controls.companyReports')}</a>
                </div>
                <div className={cls.scopeSummary}>
                    <small>{t('FinanceDashboard.controls.scope')}</small>
                    <strong>
                        {t('FinanceDashboard.controls.allActiveProjects', {
                            currency: currency.code,
                        })}
                    </strong>
                    <span>{t('FinanceDashboard.controls.allTimeBaseCurrency')}</span>
                </div>
            </section>

            <section
                className={cn(
                    cls.summary,
                    summaryIsCritical && cls.summaryCritical,
                    summaryNeedsAttention && cls.summaryWarning
                )}
            >
                <div className={cls.summaryTitle}>
                    <span className={cls.summaryDot} />
                    <strong>{economyStatus.title}</strong>
                </div>
                <p>
                    {profitComparison}{' '}
                    {riskCount === 0
                        ? t('FinanceDashboard.summary.noAttentionNeeded')
                        : t('FinanceDashboard.summary.signalsNeedAttention', {
                              count: riskCount,
                          })}
                </p>
                {riskCount > 0 && (
                    <button type="button" onClick={showRisks}>
                        {t('FinanceDashboard.actions.showRisks')} →
                    </button>
                )}
            </section>

            <section className={cls.kpiGrid} aria-label={t('FinanceDashboard.kpi.ariaLabel')}>
                <KpiCard className={cls.heroKpi}>
                    <div className={cls.kpiLabel}>{t('FinanceDashboard.kpi.profitForecast')}</div>
                    <div className={cls.kpiValue}>
                        {formatMoney(metrics.profit.forecast, currency.sign)}
                    </div>
                    <div
                        className={cn(cls.kpiMeta, varianceIsPositive ? cls.positive : cls.danger)}
                    >
                        {varianceIsPositive ? '↑' : '↓'}{' '}
                        {t('FinanceDashboard.kpi.toPlan', {
                            value: formatMoney(Math.abs(metrics.profit.variance), currency.sign),
                        })}
                    </div>
                    <div className={cls.progressTrack}>
                        <i
                            style={{
                                width: `${Math.min(
                                    100,
                                    Math.max(0, metrics.profit.profitabilityPercent)
                                )}%`,
                            }}
                        />
                    </div>
                    <div className={cls.kpiFoot}>
                        <span>{t('FinanceDashboard.kpi.profitability')}</span>
                        <strong>
                            {formatPercent(metrics.profit.profitabilityPercent, locale)}
                        </strong>
                    </div>
                </KpiCard>

                <KpiCard>
                    <div className={cls.kpiLabel}>{t('FinanceDashboard.kpi.grossProfit')}</div>
                    <div className={cls.kpiValue}>
                        {formatMoney(metrics.grossProfit.total, currency.sign)}
                    </div>
                    <div className={cn(cls.kpiMeta, cls.positive)}>
                        {t('FinanceDashboard.kpi.ofCompletion', {
                            percent: formatPercent(metrics.grossProfit.percentOfCompletion, locale),
                        })}
                    </div>
                    <div className={cn(cls.kpiFoot, cls.muted)}>
                        <span>
                            {t('FinanceDashboard.kpi.labor', {
                                value: formatMoney(metrics.grossProfit.work, currency.sign),
                            })}
                        </span>
                        <span className={cls.kpiFootEnd}>
                            {t('FinanceDashboard.kpi.materials', {
                                value: formatMoney(metrics.grossProfit.material, currency.sign),
                            })}
                        </span>
                    </div>
                </KpiCard>

                <KpiCard>
                    <div className={cls.kpiLabel}>{t('FinanceDashboard.kpi.completed')}</div>
                    <div className={cls.kpiValue}>
                        {formatMoney(metrics.completion.total, currency.sign)}
                    </div>
                    <div className={cls.kpiMeta}>
                        {t('FinanceDashboard.kpi.ofContractValue', {
                            percent: formatPercent(metrics.completion.percentOfContracts, locale),
                        })}
                    </div>
                    <div className={cn(cls.progressTrack, cls.blueTrack)}>
                        <i
                            style={{
                                width: `${Math.min(
                                    100,
                                    Math.max(0, metrics.completion.percentOfContracts ?? 0)
                                )}%`,
                            }}
                        />
                    </div>
                    <div className={cls.kpiFoot}>
                        <span>{t('FinanceDashboard.kpi.contracts')}</span>
                        <strong>{formatMoney(metrics.contracts.total, currency.sign)}</strong>
                    </div>
                </KpiCard>

                <KpiCard className={cls.warningKpi}>
                    <div className={cls.kpiLabel}>{t('FinanceDashboard.kpi.customerDebt')}</div>
                    <div className={cls.kpiValue}>
                        {formatMoney(metrics.customerDebt.total, currency.sign)}
                    </div>
                    <div className={cn(cls.kpiMeta, cls.warning)}>
                        {t('FinanceDashboard.kpi.debtConcentration', {
                            count: metrics.customerDebt.concentration.projectCount,
                            percent: formatPercent(
                                metrics.customerDebt.concentration.percent,
                                locale,
                                0
                            ),
                        })}
                    </div>
                    <button
                        className={cls.textLink}
                        type="button"
                        onClick={() =>
                            document
                                .getElementById('finance-dashboard-debt')
                                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        }
                    >
                        {t('FinanceDashboard.actions.viewProjects')} →
                    </button>
                </KpiCard>
            </section>

            <section className={cls.mainGrid}>
                <article className={cls.panel}>
                    <div className={cls.panelHead}>
                        <div>
                            <h2>{t('FinanceDashboard.grossProfit.title')}</h2>
                            <p>{t('FinanceDashboard.grossProfit.subtitle')}</p>
                        </div>
                        <div className={cls.legend}>
                            <span>
                                <i className={cls.progressLegend} />{' '}
                                {t('FinanceDashboard.grossProfit.completion')}
                            </span>
                            <span>
                                <i className={cls.profitLegend} />{' '}
                                {t('FinanceDashboard.grossProfit.profit')}
                            </span>
                        </div>
                    </div>
                    <GrossProfitTrend
                        data={charts.grossProfitByPeriod}
                        status={charts.grossProfitByPeriodStatus}
                        currencySign={currency.sign}
                    />
                </article>

                <article className={cn(cls.panel, cls.attentionPanel)} ref={attentionRef}>
                    <div className={cls.panelHead}>
                        <div>
                            <h2>{t('FinanceDashboard.attention.title')}</h2>
                            <p>{t('FinanceDashboard.attention.subtitle')}</p>
                        </div>
                        <span className={cls.riskCount}>
                            {t('FinanceDashboard.attention.riskCount', { count: riskCount })}
                        </span>
                    </div>
                    {signals.length === 0 ? (
                        <div className={cls.noRisks}>
                            <span className={cls.noRisksIcon}>✓</span>
                            <strong>{t('FinanceDashboard.attention.noCriticalSignals')}</strong>
                            <p>{t('FinanceDashboard.attention.withinNormalRange')}</p>
                        </div>
                    ) : (
                        <div className={cls.signalList}>
                            {signals.slice(0, 3).map((signal) => (
                                <AttentionSignalRow
                                    key={signal.id}
                                    signal={signal}
                                    currencySign={currency.sign}
                                />
                            ))}
                            {signals.length > 3 && (
                                <button
                                    className={cls.allSignals}
                                    type="button"
                                    onClick={() => setSignalsModalOpen(true)}
                                >
                                    {t('FinanceDashboard.actions.allSignals')} →
                                </button>
                            )}
                        </div>
                    )}
                </article>
            </section>

            <section className={cn(cls.mainGrid, cls.bottomGrid)}>
                <article className={cls.panel}>
                    <div className={cls.panelHead}>
                        <div>
                            <h2>{t('FinanceDashboard.profitPlan.title')}</h2>
                            <p>{t('FinanceDashboard.profitPlan.subtitle')}</p>
                        </div>
                        <select
                            className={cls.sortSelect}
                            value={profitSort}
                            aria-label={t('FinanceDashboard.profitPlan.sortAriaLabel')}
                            onChange={(event) =>
                                setProfitSort(event.target.value as ProfitProjectSort)
                            }
                        >
                            <option value="forecast">
                                {t('FinanceDashboard.profitPlan.sortForecast')} ↓
                            </option>
                            <option value="plan">
                                {t('FinanceDashboard.profitPlan.sortPlan')} ↓
                            </option>
                            <option value="deviation">
                                {t('FinanceDashboard.profitPlan.sortDeviation')} ↓
                            </option>
                        </select>
                    </div>
                    <div className={cls.comparisonLegend}>
                        <span>
                            <i className={cls.planLegend} /> {t('FinanceDashboard.profitPlan.plan')}
                        </span>
                        <span>
                            <i className={cls.forecastLegend} />{' '}
                            {t('FinanceDashboard.profitPlan.forecast')}
                        </span>
                    </div>
                    <ProfitComparison
                        projects={charts.profitPlan.projects}
                        currencySign={currency.sign}
                        sort={profitSort}
                    />
                </article>

                <article className={cn(cls.panel, cls.debtPanel)} id="finance-dashboard-debt">
                    <div className={cls.panelHead}>
                        <div>
                            <h2>{t('FinanceDashboard.customerDebt.title')}</h2>
                            <p>{t('FinanceDashboard.customerDebt.subtitle')}</p>
                        </div>
                        <strong className={cls.panelTotal}>
                            {formatMoney(charts.customerDebt.total, currency.sign)}
                        </strong>
                    </div>
                    {charts.customerDebt.projects.length === 0 ? (
                        <div className={cls.emptyPanel}>
                            {t('FinanceDashboard.customerDebt.empty')}
                        </div>
                    ) : (
                        <div className={cls.debtRows}>
                            {charts.customerDebt.projects.map((project, index) => (
                                <div className={cls.debtRow} key={project.projectId}>
                                    <ProjectLink
                                        className={cls.debtName}
                                        projectId={project.projectId}
                                        projectName={project.projectName}
                                    />
                                    <div className={cls.debtTrack}>
                                        <i
                                            className={cls.debtBar}
                                            style={{
                                                width: `${(project.value / debtMaximum) * 100}%`,
                                                animationDelay: `${index * 80}ms`,
                                            }}
                                        />
                                    </div>
                                    <strong>{formatMoney(project.value, currency.sign)}</strong>
                                </div>
                            ))}
                        </div>
                    )}
                </article>
            </section>

            <footer className={cls.footer}>
                <span>{t('FinanceDashboard.footer.calculationScope')}</span>
                {availability.pending.length > 0 && (
                    <span>
                        {t('FinanceDashboard.footer.pendingIndicators', {
                            count: availability.pending.length,
                        })}
                    </span>
                )}
            </footer>
            <CustomModal
                isOpen={signalsModalOpen}
                onHideModal={() => setSignalsModalOpen(false)}
                contentClasses={cn(cls.dashboardModal, cls.signalsModal)}
            >
                <CustomModal.Header>
                    {t('FinanceDashboard.actions.allSignals')}
                    <span className={cls.modalRiskCount}>{riskCount}</span>
                </CustomModal.Header>
                <CustomModal.Body classes={cls.signalsModalBody} width="min(680px, 82vw)">
                    <div className={cls.modalSignalList}>
                        {signals.map((signal) => (
                            <AttentionSignalRow
                                key={signal.id}
                                signal={signal}
                                currencySign={currency.sign}
                            />
                        ))}
                    </div>
                </CustomModal.Body>
            </CustomModal>
            <CustomModal
                isOpen={calculationModalOpen && isBackgroundCalculation}
                onHideModal={() => setCalculationModalOpen(false)}
                contentClasses={cn(cls.dashboardModal, cls.calculationModal)}
            >
                <CustomModal.Header>
                    {t('FinanceDashboard.calculation.refreshingOverview')}
                </CustomModal.Header>
                <CustomModal.Body classes={cls.calculationModalBody} width="100%">
                    <CalculationProgress state={data} embedded />
                </CustomModal.Body>
            </CustomModal>
        </main>
    );
};
